// =====================================================================================
// PerformanceMonitor - Monitors video call performance metrics
// =====================================================================================
// Tracks video quality, network performance, and system resources
// Provides insights for optimization and debugging

interface PerformanceMetrics {
  // Video metrics
  videoFrameRate: number;
  videoBitrate: number;
  videoResolution: { width: number; height: number };
  videoPacketsLost: number;

  // Audio metrics
  audioBitrate: number;
  audioPacketsLost: number;

  // Network metrics
  networkLatency: number;
  networkJitter: number;
  bandwidthEstimate: number;

  // System metrics
  cpuUsage: number;
  memoryUsage: number;

  // Connection quality
  overallQuality: 'excellent' | 'good' | 'fair' | 'poor';
  timestamp: number;
}

interface PerformanceThresholds {
  frameRate: { good: number; fair: number };
  bitrate: { good: number; fair: number };
  latency: { good: number; fair: number };
  packetLoss: { good: number; fair: number };
}

export class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = [];
  private monitoringInterval: NodeJS.Timeout | null = null;
  private readonly maxHistorySize = 60; // Keep 60 samples (5 minutes at 5s intervals)

  private readonly thresholds: PerformanceThresholds = {
    frameRate: { good: 24, fair: 15 },
    bitrate: { good: 800_000, fair: 300_000 },
    latency: { good: 100, fair: 200 },
    packetLoss: { good: 1, fair: 5 }
  };

  constructor() {
    console.info('🎥 📊 PerformanceMonitor initialized');
  }

  /**
   * Start monitoring performance metrics
   */
  startMonitoring(room: any): void {
    if (this.monitoringInterval) {
      this.stopMonitoring();
    }

    console.info('🎥 📊 Starting performance monitoring');

    this.monitoringInterval = setInterval(() => {
      this.collectMetrics(room);
    }, 5000); // Collect metrics every 5 seconds
  }

  /**
   * Stop monitoring performance metrics
   */
  stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
      console.info('🎥 📊 Stopped performance monitoring');
    }
  }

  /**
   * Collect current performance metrics
   */
  private async collectMetrics(room: any): Promise<void> {
    if (!room || room.state !== 'connected') return;

    try {
      // Get connection stats from LiveKit room
      const stats = await room.getConnectionQuality();
      const localParticipant = room.localParticipant;

      // Get video track stats
      const videoPublication = localParticipant?.getTrackPublication('camera');
      const audioPublication = localParticipant?.getTrackPublication('microphone');

      const metrics: PerformanceMetrics = {
        // Video metrics
        videoFrameRate: this.extractVideoFrameRate(stats),
        videoBitrate: this.extractVideoBitrate(stats),
        videoResolution: this.extractVideoResolution(videoPublication),
        videoPacketsLost: stats?.packetsLost || 0,

        // Audio metrics
        audioBitrate: this.extractAudioBitrate(stats),
        audioPacketsLost: stats?.audioPacketsLost || 0,

        // Network metrics
        networkLatency: stats?.rtt || 0,
        networkJitter: stats?.jitter || 0,
        bandwidthEstimate: stats?.availableOutgoingBitrate || 0,

        // System metrics (simplified for web environment)
        cpuUsage: this.estimateCPUUsage(),
        memoryUsage: this.getMemoryUsage(),

        // Overall quality assessment
        overallQuality: this.calculateOverallQuality(stats),
        timestamp: Date.now()
      };

      // Add to history
      this.addMetric(metrics);

      // Log significant performance issues
      this.checkPerformanceIssues(metrics);

    } catch (error) {
      console.warn('🎥 📊 Failed to collect performance metrics:', error);
    }
  }

  /**
   * Add metric to history with size limiting
   */
  private addMetric(metric: PerformanceMetrics): void {
    this.metrics.push(metric);

    // Keep only the last N metrics
    if (this.metrics.length > this.maxHistorySize) {
      this.metrics = this.metrics.slice(-this.maxHistorySize);
    }
  }

  /**
   * Extract video frame rate from connection stats
   */
  private extractVideoFrameRate(stats: any): number {
    return stats?.videoFrameRate || stats?.framesPerSecond || 0;
  }

  /**
   * Extract video bitrate from connection stats
   */
  private extractVideoBitrate(stats: any): number {
    return stats?.videoBitrate || stats?.outgoingBitrate || 0;
  }

  /**
   * Extract video resolution from track publication
   */
  private extractVideoResolution(videoPublication: any): { width: number; height: number } {
    if (videoPublication?.track?.mediaStreamTrack) {
      const settings = videoPublication.track.mediaStreamTrack.getSettings();
      return {
        width: settings.width || 0,
        height: settings.height || 0
      };
    }
    return { width: 0, height: 0 };
  }

  /**
   * Extract audio bitrate from connection stats
   */
  private extractAudioBitrate(stats: any): number {
    return stats?.audioBitrate || 0;
  }

  /**
   * Estimate CPU usage (simplified for web environment)
   */
  private estimateCPUUsage(): number {
    // In a web environment, we can't directly measure CPU usage
    // This is a rough estimate based on frame timing
    if ('performance' in window && 'now' in performance) {
      // Use a simple heuristic based on performance timing
      const start = performance.now();
      // Perform a small CPU-intensive task
      for (let i = 0; i < 1000; i++) {
        Math.random();
      }
      const duration = performance.now() - start;

      // Convert to a rough percentage (this is very approximate)
      return Math.min(duration * 10, 100);
    }
    return 0;
  }

  /**
   * Get memory usage information
   */
  private getMemoryUsage(): number {
    // Use performance.memory API if available (Chrome only)
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return memory.usedJSHeapSize / memory.totalJSHeapSize * 100;
    }
    return 0;
  }

  /**
   * Calculate overall connection quality
   */
  private calculateOverallQuality(stats: any): PerformanceMetrics['overallQuality'] {
    const latency = stats?.rtt || 0;
    const packetLoss = stats?.packetsLost || 0;
    const bitrate = stats?.outgoingBitrate || 0;

    // Score based on different factors
    let score = 100;

    // Latency scoring
    if (latency > this.thresholds.latency.fair) score -= 30;
    else if (latency > this.thresholds.latency.good) score -= 15;

    // Packet loss scoring
    if (packetLoss > this.thresholds.packetLoss.fair) score -= 30;
    else if (packetLoss > this.thresholds.packetLoss.good) score -= 15;

    // Bitrate scoring
    if (bitrate < this.thresholds.bitrate.fair) score -= 20;
    else if (bitrate < this.thresholds.bitrate.good) score -= 10;

    // Convert score to quality rating
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'poor';
  }

  /**
   * Check for performance issues and log warnings
   */
  private checkPerformanceIssues(metrics: PerformanceMetrics): void {
    const issues: string[] = [];

    if (metrics.videoFrameRate < this.thresholds.frameRate.fair) {
      issues.push(`Low frame rate: ${metrics.videoFrameRate} fps`);
    }

    if (metrics.networkLatency > this.thresholds.latency.fair) {
      issues.push(`High latency: ${metrics.networkLatency} ms`);
    }

    if (metrics.videoPacketsLost > this.thresholds.packetLoss.fair) {
      issues.push(`High packet loss: ${metrics.videoPacketsLost}%`);
    }

    if (metrics.cpuUsage > 80) {
      issues.push(`High CPU usage: ${metrics.cpuUsage}%`);
    }

    if (issues.length > 0) {
      console.warn('🎥 📊 Performance issues detected:', issues.join(', '));
    }
  }

  /**
   * Get current performance metrics
   */
  getCurrentMetrics(): PerformanceMetrics | null {
    return this.metrics.length > 0 ? this.metrics[this.metrics.length - 1] : null;
  }

  /**
   * Get performance history
   */
  getMetricsHistory(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  /**
   * Get average metrics over the last N samples
   */
  getAverageMetrics(samples: number = 10): Partial<PerformanceMetrics> | null {
    if (this.metrics.length === 0) return null;

    const recentMetrics = this.metrics.slice(-samples);
    const count = recentMetrics.length;

    return {
      videoFrameRate: recentMetrics.reduce((sum, m) => sum + m.videoFrameRate, 0) / count,
      videoBitrate: recentMetrics.reduce((sum, m) => sum + m.videoBitrate, 0) / count,
      networkLatency: recentMetrics.reduce((sum, m) => sum + m.networkLatency, 0) / count,
      videoPacketsLost: recentMetrics.reduce((sum, m) => sum + m.videoPacketsLost, 0) / count,
      cpuUsage: recentMetrics.reduce((sum, m) => sum + m.cpuUsage, 0) / count,
      memoryUsage: recentMetrics.reduce((sum, m) => sum + m.memoryUsage, 0) / count
    };
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    current: PerformanceMetrics | null;
    average: Partial<PerformanceMetrics> | null;
    quality: string;
    recommendations: string[];
  } {
    const current = this.getCurrentMetrics();
    const average = this.getAverageMetrics();
    const recommendations: string[] = [];

    if (current) {
      // Generate recommendations based on current performance
      if (current.videoFrameRate < this.thresholds.frameRate.good) {
        recommendations.push('Consider reducing video quality or resolution');
      }

      if (current.networkLatency > this.thresholds.latency.good) {
        recommendations.push('Check network connection stability');
      }

      if (current.videoPacketsLost > this.thresholds.packetLoss.good) {
        recommendations.push('Network issues detected, consider switching networks');
      }

      if (current.cpuUsage > 70) {
        recommendations.push('High system load, close other applications');
      }
    }

    return {
      current,
      average,
      quality: current?.overallQuality || 'unknown',
      recommendations
    };
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): string {
    return JSON.stringify({
      metrics: this.metrics,
      summary: this.getPerformanceSummary(),
      exportedAt: new Date().toISOString()
    }, null, 2);
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.metrics = [];
    console.info('🎥 📊 Performance metrics reset');
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stopMonitoring();
    this.reset();
    console.info('🎥 📊 PerformanceMonitor cleaned up');
  }
}

// Singleton instance for global access
export const performanceMonitor = new PerformanceMonitor();